/**
 * Catálogo de produtos — a tela que responde "este produto está vendável".
 *
 * A tira de grade é a peça central, e ela é o motivo de esta tela existir
 * separada do estoque: o estoque mostra célula por célula ("quantas do 42
 * preto"), o catálogo mostra a COLUNA ("o 42 acabou em todas as cores"). A
 * segunda pergunta é a que faz o lojista comprar.
 *
 * Três coisas esta tela NÃO faz:
 *
 * 1. Não calcula saldo nem situação. Quem deriva "grade furada" é o servidor,
 *    a partir de `saldo_sku`.
 * 2. Não decide quem vê custo. O bloco `custos` simplesmente não vem para
 *    quem não tem `cost.read` (CONSTITUICAO.md §4.1) — a tela não desenha o
 *    que não recebeu.
 * 3. Não inventa giro, venda 30d nem curva ABC. O bloco de desempenho declara
 *    a fonte, e quando ela é `aguarda_venda` a tela diz o que falta.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { Botao, LinkBotao } from '../../componentes/Botao'
import { BotaoExportar } from '../../componentes/BotaoExportar'
import { Campo } from '../../componentes/Campos'
import { Dialogo } from '../../componentes/Dialogo'
import type { ColunaExportacao } from '../../componentes/exportacao'
import { usarFocoPreso } from '../../componentes/foco'
import { CabecalhoDaGaveta, CorpoDaGaveta, Gaveta, RodapeDaGaveta } from '../../componentes/Gaveta'
import { Segmentado } from '../../componentes/Segmentado'
import { Icone, type NomeIcone } from '../../componentes/Icone'
import { MenuDeLinha, type AcaoDeLinha } from '../../componentes/Listagem'
import { ErroApi } from '../../plataforma/api'
import { formatarMoeda } from '../../plataforma/dinheiro'
import { useSessao } from '../../plataforma/contexto-sessao'
import {
  adicionarCodigoAlternativo,
  alternarAtivo,
  carregarCatalogo,
  carregarFicha,
  formatarPontos,
  removerCodigoAlternativo,
  riscoDaCelula,
  riscoDoTamanho,
  urlDaCapa,
  urlDaFoto,
  FILTROS_SITUACAO,
  MAX_CODIGOS_ALTERNATIVOS,
  type Catalogo,
  type CelulaFicha,
  type Categoria,
  type Cor,
  type Ficha,
  type Foto,
  type LinhaProduto,
  type PosicaoGrade,
  type Situacao,
  type VariacaoFicha,
} from '../../plataforma/produtos'
import { LayoutApp } from '../app/LayoutApp'

type Visao = 'lista' | 'grade'

/**
 * As colunas do CSV e da view impressa. `veCusto` é parâmetro, e não uma
 * constante de módulo como nas duas telas irmãs: a coluna de custo só existe
 * para quem tem `cost.read`, e o servidor simplesmente OMITE o bloco de quem
 * não tem — exportar uma coluna de custo vazia para essa pessoa seria a tela
 * inventando um dado que a API nunca mandou (CONSTITUICAO.md §4.1).
 */
function colunasExportacaoProdutos(veCusto: boolean): ColunaExportacao<LinhaProduto>[] {
  const colunas: ColunaExportacao<LinhaProduto>[] = [
    { cabecalho: 'Código', valor: (p) => p.codigo },
    { cabecalho: 'Produto', valor: (p) => p.nome },
    { cabecalho: 'Categoria', valor: (p) => p.categoria_rotulo },
    { cabecalho: 'Marca', valor: (p) => p.marca ?? '' },
    { cabecalho: 'Preço', valor: (p) => formatarMoeda(p.preco_centavos), alinhamento: 'direita' },
  ]
  if (veCusto) {
    colunas.push({
      cabecalho: 'Custo',
      valor: (p) => (p.custos ? formatarMoeda(p.custos.custo_centavos) : ''),
      alinhamento: 'direita',
    })
  }
  colunas.push(
    { cabecalho: 'Estoque', valor: (p) => String(p.pecas), alinhamento: 'direita' },
    { cabecalho: 'SKUs', valor: (p) => String(p.skus), alinhamento: 'direita' },
    { cabecalho: 'Situação', valor: (p) => p.situacao_rotulo },
  )
  return colunas
}

export function TelaProdutos() {
  const { podeVer } = useSessao()
  const podeCadastrar = podeVer('product.write')
  const navegar = useNavigate()

  const [busca, setBusca] = useState('')
  const [buscaAtiva, setBuscaAtiva] = useState('')
  const [categoria, setCategoria] = useState<Categoria | ''>('')
  const [situacao, setSituacao] = useState<Situacao | ''>('')
  const [inativos, setInativos] = useState(false)
  const [visao, setVisao] = useState<Visao>('lista')
  const [filial, setFilial] = useState('')

  const [catalogo, setCatalogo] = useState<Catalogo | null>(null)
  const [erro, setErro] = useState('')

  // Aberto por CÓDIGO, não pelo objeto: o drawer busca a ficha completa, que
  // tem preço e custo por SKU e não cabe na listagem.
  const [aberto, setAberto] = useState('')

  // `mexendo` é o código do produto cuja ativação está no ar, e `recarga`
  // força a lista a refazer a consulta depois de uma mudança. Atualizar a
  // linha em memória seria mais rápido e mentiria no primeiro caso em que o
  // servidor decidisse diferente do que a tela supôs.
  const [mexendo, setMexendo] = useState('')
  const [recarga, setRecarga] = useState(0)

  // Debounce da busca: cada tecla disparando uma consulta que faz JOIN em
  // quatro tabelas é como se derruba o próprio banco em dia de movimento.
  useEffect(() => {
    const relogio = setTimeout(() => setBuscaAtiva(busca), 300)
    return () => clearTimeout(relogio)
  }, [busca])

  useEffect(() => {
    const controle = new AbortController()

    carregarCatalogo({ busca: buscaAtiva, categoria, situacao, filial, inativos }, controle.signal)
      .then((dados) => {
        setCatalogo(dados)
        setErro('')
      })
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível carregar o catálogo.')
      })

    return () => controle.abort()
  }, [buscaAtiva, categoria, situacao, filial, inativos, recarga])

  /**
   * Liga e desliga o produto pelo menu ⋮.
   *
   * Manda SÓ o `ativo`: a linha da listagem é um recorte do produto, e
   * reenviar o que ela tem seria mandar um PUT sem a grade — que o contrato
   * define como "não mexa", mas que ficaria a um descuido de virar "apague".
   *
   * Ao inativar sem o filtro de inativos ligado, a linha SOME da lista. O
   * filtro é ligado junto para a pessoa continuar vendo o que acabou de fazer
   * — e para ter como desfazer.
   */
  const alternar = async (produto: LinhaProduto) => {
    setMexendo(produto.codigo)
    try {
      await alternarAtivo(produto.codigo, !produto.ativo)
      if (produto.ativo) setInativos(true)
      setRecarga((n) => n + 1)
      setErro('')
    } catch (causa: unknown) {
      setErro(
        causa instanceof ErroApi
          ? causa.message
          : `Não foi possível ${produto.ativo ? 'inativar' : 'ativar'} o produto.`,
      )
    } finally {
      setMexendo('')
    }
  }

  const subtitulo = catalogo
    ? `${catalogo.filial.nome} · ${contar(catalogo.resumo.ativos, 'produto', 'produtos')} · ${contar(catalogo.resumo.skus, 'SKU', 'SKUs')}`
    : 'Carregando…'

  const rotuloDaCategoria = catalogo?.categorias.find((aba) => aba.chave === categoria)?.rotulo
  const rotuloDaSituacao = FILTROS_SITUACAO.find((f) => f.chave === situacao)?.rotulo
  const filtroAplicado = [
    catalogo && `Unidade: ${catalogo.filial.nome}`,
    buscaAtiva && `Busca: "${buscaAtiva}"`,
    categoria && rotuloDaCategoria && `Categoria: ${rotuloDaCategoria}`,
    situacao && rotuloDaSituacao && `Situação: ${rotuloDaSituacao}`,
    inativos && 'Incluindo inativos',
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <LayoutApp
      titulo="Catálogo de produtos"
      subtitulo={subtitulo}
      acoes={
        <>
          {catalogo && (
            <BotaoExportar
              nomeBase="produtos"
              titulo="Catálogo de produtos"
              filtroAplicado={filtroAplicado || undefined}
              colunas={colunasExportacaoProdutos(catalogo.ve_custo)}
              linhas={catalogo.produtos}
            />
          )}
          {podeCadastrar && (
            <LinkBotao para="/app/produtos/novo" icone="add">
              Novo produto
            </LinkBotao>
          )}
        </>
      }
    >
      <div className="flex w-full flex-col gap-4">
        {erro && (
          <div role="alert" className="rounded-sistema bg-erro-suave px-4 py-3 text-sm text-erro-texto">
            {erro}
          </div>
        )}

        <BlocoDeKPIs catalogo={catalogo} />

        <div className="overflow-hidden rounded-sistema border border-borda bg-superficie">
          <Toolbar
            catalogo={catalogo}
            busca={busca}
            aoBuscar={setBusca}
            categoria={categoria}
            aoTrocarCategoria={setCategoria}
            situacao={situacao}
            aoTrocarSituacao={setSituacao}
            inativos={inativos}
            aoTrocarInativos={setInativos}
            visao={visao}
            aoTrocarVisao={setVisao}
          />

          {catalogo && catalogo.filiais.length > 1 && (
            <div className="flex flex-wrap items-center gap-2.5 border-b border-borda px-4 py-2.5">
              <span className="text-[12.5px] text-texto-suave">Saldo da unidade</span>
              <SeletorDeFilial
                filiais={catalogo.filiais}
                atual={catalogo.filial.codigo}
                aoTrocar={setFilial}
              />
            </div>
          )}

          {!catalogo ? (
            <div className="h-[280px] animate-pulse bg-superficie-suave" />
          ) : catalogo.produtos.length === 0 ? (
            <EstadoVazio
              busca={buscaAtiva}
              temFiltro={categoria !== '' || situacao !== ''}
              podeCadastrar={podeCadastrar}
            />
          ) : visao === 'lista' ? (
            <TabelaDeProdutos
              produtos={catalogo.produtos}
              veCusto={catalogo.ve_custo}
              podeEditar={podeCadastrar}
              mexendo={mexendo}
              aoAbrir={setAberto}
              aoEditar={(codigo) => navegar(`/app/produtos/${encodeURIComponent(codigo)}/editar`)}
              aoAlternar={(produto) => void alternar(produto)}
            />
          ) : (
            <CartoesDeProduto produtos={catalogo.produtos} aoAbrir={setAberto} />
          )}

          {catalogo && catalogo.produtos.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-borda px-4 py-2.5">
              <span className="tabular text-[13px] text-texto-suave">
                {contar(catalogo.total, 'produto', 'produtos')}
              </span>
              <span className="tabular text-[12.5px] text-texto-fraco">
                {contar(catalogo.resumo.skus, 'SKU', 'SKUs')} na seleção
              </span>
            </div>
          )}
        </div>
      </div>

      {aberto && (
        <DrawerDaFicha
          codigo={aberto}
          filial={filial}
          podeEditar={podeCadastrar}
          aoFechar={() => setAberto('')}
        />
      )}
    </LayoutApp>
  )
}

/* ------------------------------------------------------------------- KPIs */

function BlocoDeKPIs({ catalogo }: { catalogo: Catalogo | null }) {
  if (!catalogo) {
    return (
      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-[104px] animate-pulse rounded-sistema bg-superficie" />
        ))}
      </div>
    )
  }

  const { resumo } = catalogo

  const kpis: { rotulo: string; valor: string; dica: string; icone: NomeIcone; cor?: string }[] = [
    {
      rotulo: 'Produtos no catálogo',
      valor: String(resumo.ativos),
      dica: `${resumo.skus} ${resumo.skus === 1 ? 'SKU' : 'SKUs'} de cor × tamanho`,
      icone: 'sell',
    },
    {
      rotulo: 'Grades furadas',
      valor: String(resumo.grades_furadas),
      dica: 'ao menos um tamanho zerado',
      icone: 'warning',
      cor: resumo.grades_furadas > 0 ? 'text-atencao-texto' : undefined,
    },
    {
      rotulo: 'Esgotados',
      valor: String(resumo.esgotados),
      dica: 'grade inteira sem peça',
      icone: 'error',
      cor: resumo.esgotados > 0 ? 'text-erro' : undefined,
    },
  ]

  // Os dois KPIs de dinheiro só existem para quem tem `cost.read`. Quando não
  // vêm, o quarto cartão é substituído — e não deixado vazio, que faria a
  // grade de quatro colunas parecer quebrada.
  if (resumo.valor_estoque_custo_centavos !== undefined) {
    kpis.push({
      rotulo: 'Valor em estoque',
      valor: formatarMoeda(resumo.valor_estoque_custo_centavos),
      dica:
        resumo.margem_media_pontos_base !== undefined
          ? `margem média de ${formatarPontos(resumo.margem_media_pontos_base)}`
          : 'a preço de custo',
      icone: 'payments',
    })
  } else {
    kpis.push({
      rotulo: 'Tamanhos zerados',
      valor: String(
        catalogo.produtos.reduce((total, produto) => total + produto.tamanhos_zerados, 0),
      ),
      dica: 'na seleção atual',
      icone: 'inventory_2',
    })
  }

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      {kpis.map((kpi) => (
        <div
          key={kpi.rotulo}
          className="rounded-sistema border border-borda bg-superficie px-4 py-3.5 shadow-[var(--shadow-cartao)]"
        >
          <div className="mb-2.5 flex items-center gap-2 text-[12.5px] text-texto-suave">
            <Icone nome={kpi.icone} tamanho={18} className="text-texto-fraco" />
            {kpi.rotulo}
          </div>
          <div className={`tabular text-[21px] font-bold ${kpi.cor ?? 'text-tinta'}`}>
            {kpi.valor}
          </div>
          <div className="mt-1 text-xs text-texto-suave">{kpi.dica}</div>
        </div>
      ))}
    </div>
  )
}

/* ---------------------------------------------------------------- toolbar */

function Toolbar({
  catalogo,
  busca,
  aoBuscar,
  categoria,
  aoTrocarCategoria,
  situacao,
  aoTrocarSituacao,
  inativos,
  aoTrocarInativos,
  visao,
  aoTrocarVisao,
}: {
  catalogo: Catalogo | null
  busca: string
  aoBuscar: (texto: string) => void
  categoria: Categoria | ''
  aoTrocarCategoria: (valor: Categoria | '') => void
  situacao: Situacao | ''
  aoTrocarSituacao: (valor: Situacao | '') => void
  inativos: boolean
  aoTrocarInativos: (valor: boolean) => void
  visao: Visao
  aoTrocarVisao: (valor: Visao) => void
}) {
  return (
    // `flex-wrap` e `min-h`, nunca `height` fixa: regra 5 do "layout que já
    // custou bug" da spec 01 — header com muitos controles quebra em duas
    // linhas em tela estreita, e altura fixa corta a segunda.
    <div className="flex min-h-[54px] flex-wrap items-center gap-2.5 border-b border-borda px-4 py-3">
      {/* `basis-[230px]` + `min-w-0`: regra 3 — input dentro de composto
          precisa dos dois, ou estica o flex e empurra os irmãos para fora. */}
      <label className="composto h-[34px] min-w-0 flex-1 basis-[230px] cursor-text">
        <Icone nome="search" tamanho={18} className="text-texto-fraco" />
        <input
          value={busca}
          onChange={(evento) => aoBuscar(evento.target.value)}
          placeholder="Buscar nome, código, marca ou código de barras"
        />
        <span className="sr-only">Buscar no catálogo</span>
      </label>

      {/* `filtro` e não `aba`: a categoria RECORTA a mesma lista de produtos,
          não troca o assunto da tela. `role="tab"` prometeria um painel que
          não existe. */}
      <Segmentado
        opcoes={[
          { chave: '' as Categoria | '', rotulo: 'Todas' },
          ...(catalogo?.categorias ?? []).map((aba) => ({
            chave: aba.chave as Categoria | '',
            rotulo: aba.rotulo,
            contagem: aba.produtos,
          })),
        ]}
        valor={categoria}
        aoTrocar={aoTrocarCategoria}
        rotuloDoGrupo="Categoria"
        compacto
      />

      <div className="flex-1" />

      <label className="sr-only" htmlFor="filtro-situacao">
        Situação
      </label>
      <select
        id="filtro-situacao"
        value={situacao}
        onChange={(evento) => aoTrocarSituacao(evento.target.value as Situacao | '')}
        className="campo h-[34px] w-auto"
      >
        {FILTROS_SITUACAO.map((filtro) => (
          <option key={filtro.chave || 'todas'} value={filtro.chave}>
            {filtro.rotulo}
          </option>
        ))}
      </select>

      {/*
        O caminho de volta do "Inativar" do menu ⋮. Sem ele, inativar seria uma
        porta que só abre para fora: a linha some da lista e não existe tela
        nenhuma onde reativá-la.
      */}
      <button
        type="button"
        aria-pressed={inativos}
        onClick={() => aoTrocarInativos(!inativos)}
        className={`flex h-[34px] items-center gap-2 rounded-sistema border px-3.5 text-[12.5px] font-semibold ${
          inativos
            ? 'border-marca bg-marca-suave text-marca-forte'
            : 'border-borda bg-superficie text-texto hover:border-inativo'
        }`}
      >
        <Icone nome={inativos ? 'visibility' : 'lock'} tamanho={16} />
        Mostrar inativos
      </button>

      <Segmentado
        opcoes={[
          {
            chave: 'lista' as Visao,
            rotulo: <Icone nome="view_list" tamanho={19} />,
            rotuloAcessivel: 'Ver em lista',
          },
          {
            chave: 'grade' as Visao,
            rotulo: <Icone nome="grid_view" tamanho={19} />,
            rotuloAcessivel: 'Ver em cartões',
          },
        ]}
        valor={visao}
        aoTrocar={aoTrocarVisao}
        rotuloDoGrupo="Modo de visualização"
        compacto
      />
    </div>
  )
}


function SeletorDeFilial({
  filiais,
  atual,
  aoTrocar,
}: {
  filiais: { codigo: string; nome: string }[]
  atual: string
  aoTrocar: (codigo: string) => void
}) {
  return (
    <Segmentado
      opcoes={filiais.map((filial) => ({ chave: filial.codigo, rotulo: filial.nome }))}
      valor={atual}
      aoTrocar={aoTrocar}
      rotuloDoGrupo="Unidade"
      compacto
    />
  )
}

/* ----------------------------------------------------------------- tabela */

function TabelaDeProdutos({
  produtos,
  veCusto,
  podeEditar,
  mexendo,
  aoAbrir,
  aoEditar,
  aoAlternar,
}: {
  produtos: LinhaProduto[]
  veCusto: boolean
  podeEditar: boolean
  mexendo: string
  aoAbrir: (codigo: string) => void
  aoEditar: (codigo: string) => void
  aoAlternar: (produto: LinhaProduto) => void
}) {
  const colunas = ['Produto', 'Grade', 'Estoque', 'Preço']
  if (veCusto) colunas.push('Margem')
  colunas.push('Situação', 'Ações')

  return (
    // Wrapper com `overflow-x-auto` + `min-width` na tabela: regra 6 do
    // "layout que já custou bug". Card com `overflow:hidden` sozinho CORTA a
    // última coluna em vez de deixar rolar.
    <div className="overflow-x-auto">
      <table className="w-full min-w-[900px] border-collapse">
        <caption className="sr-only">Produtos do catálogo com saldo por tamanho</caption>
        <thead>
          <tr className="bg-fundo">
            {colunas.map((titulo) => (
              <th
                key={titulo}
                scope="col"
                className={`border-b border-borda px-3 py-2.5 text-[12.5px] font-semibold text-texto-suave ${
                  titulo === 'Estoque' || titulo === 'Preço' || titulo === 'Margem'
                    ? 'text-right'
                    : 'text-left'
                }`}
              >
                {/* "Ações" tem cabeçalho para o leitor de tela, e não para o
                    olho: a coluna do ⋮ não precisa de rótulo desenhado. */}
                {titulo === 'Ações' ? <span className="sr-only">Ações</span> : titulo}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {produtos.map((produto) => (
            <LinhaDaTabela
              key={produto.codigo}
              produto={produto}
              veCusto={veCusto}
              podeEditar={podeEditar}
              mexendo={mexendo === produto.codigo}
              aoAbrir={aoAbrir}
              aoEditar={aoEditar}
              aoAlternar={aoAlternar}
            />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function LinhaDaTabela({
  produto,
  veCusto,
  podeEditar,
  mexendo,
  aoAbrir,
  aoEditar,
  aoAlternar,
}: {
  produto: LinhaProduto
  veCusto: boolean
  podeEditar: boolean
  mexendo: boolean
  aoAbrir: (codigo: string) => void
  aoEditar: (codigo: string) => void
  aoAlternar: (produto: LinhaProduto) => void
}) {
  const celula = 'border-b border-divisoria px-3 py-2.5'

  const acoes: AcaoDeLinha[] = [
    { rotulo: 'Ver ficha', icone: 'visibility', aoEscolher: () => aoAbrir(produto.codigo) },
  ]

  // Sem `product.write` os dois itens de escrita nem aparecem — spec 04: item
  // desabilitado ensina que a função existe e gera a mesma pergunta toda
  // semana. `razaoImpedida` fica para o que é impedimento de ESTADO, que é
  // informação nova para quem já tem a permissão.
  if (podeEditar) {
    acoes.push(
      {
        rotulo: 'Editar',
        icone: 'edit_note',
        aoEscolher: () => aoEditar(produto.codigo),
        razaoImpedida: mexendo ? 'aguardando o servidor responder' : undefined,
      },
      {
        rotulo: produto.ativo ? 'Inativar' : 'Ativar',
        icone: produto.ativo ? 'lock' : 'check_circle',
        aoEscolher: () => aoAlternar(produto),
        separar: true,
        destrutiva: produto.ativo,
        razaoImpedida: mexendo ? 'aguardando o servidor responder' : undefined,
      },
    )
  }

  return (
    <tr className={produto.ativo ? undefined : 'bg-superficie-suave'}>
      <th scope="row" className={`${celula} text-left font-normal`}>
        <span className="flex items-center gap-3">
          <Miniatura codigo={produto.codigo} fotos={produto.fotos} nome={produto.nome} />
          <span className="min-w-0">
            <button
              type="button"
              onClick={() => aoAbrir(produto.codigo)}
              className="block max-w-full truncate text-left text-[13.5px] font-semibold text-tinta hover:text-marca"
            >
              {produto.nome}
            </button>
            <span className="tabular mt-0.5 block text-xs text-texto-suave">
              {produto.codigo}
              {produto.marca ? ` · ${produto.marca}` : ''} · {produto.categoria_rotulo}
            </span>
          </span>
        </span>
      </th>

      <td className={celula}>
        <TiraDeGrade tira={produto.tira_grade} />
        <span className="mt-[7px] flex items-center gap-2">
          <BolinhasDeCor cores={produto.cores} />
          <span className="tabular text-[11.5px] text-texto-suave">
            {produto.skus} {produto.skus === 1 ? 'SKU' : 'SKUs'}
          </span>
        </span>
      </td>

      <td className={`${celula} text-right`}>
        <span className="tabular block text-sm font-bold text-tinta">{produto.pecas}</span>
        {/* "Vendas 30d" do protótipo depende do módulo de venda, que não
            existe. No lugar vai um número verdadeiro: quantos tamanhos da
            grade estão zerados. */}
        <span className="tabular mt-0.5 block text-[11.5px] text-texto-suave">
          {produto.tamanhos_zerados === 0
            ? 'grade completa'
            : `${produto.tamanhos_zerados} ${produto.tamanhos_zerados === 1 ? 'tam. zerado' : 'tam. zerados'}`}
        </span>
      </td>

      <td className={`${celula} text-right`}>
        <span className="tabular block text-[13.5px] font-semibold text-tinta">
          {formatarMoeda(produto.preco_centavos)}
        </span>
        {produto.custos && (
          <span className="tabular mt-0.5 block text-[11.5px] text-texto-suave">
            custo {formatarMoeda(produto.custos.custo_centavos)}
          </span>
        )}
      </td>

      {veCusto && (
        <td className={`${celula} text-right`}>
          <SeloDeMargem pontos={produto.custos?.margem_pontos_base} />
        </td>
      )}

      <td className={celula}>
        <SeloDeSituacao situacao={produto.situacao} rotulo={produto.situacao_rotulo} />
        {/* Inativo é a coluna `produto.ativo`, e não a situação derivada do
            saldo. Um produto inativo pode estar cheio de peça no depósito — e
            por isso os dois selos convivem em vez de um substituir o outro. */}
        {!produto.ativo && (
          <span className="mt-1 flex items-center gap-1.5 text-[11.5px] font-semibold text-texto-suave">
            <Icone nome="lock" tamanho={13} />
            Inativo
          </span>
        )}
      </td>

      <td className={`${celula} pr-4`}>
        <MenuDeLinha rotulo={`Ações de ${produto.nome}`} acoes={acoes} />
      </td>
    </tr>
  )
}

/**
 * A capa na listagem.
 *
 * `<img>` com a URL da API, e não um blob carregado por fetch: o cookie de
 * sessão viaja igual e o navegador cacheia sozinho (o servidor manda
 * `Cache-Control: private`). Produto sem foto mantém o retângulo cinza — o
 * espaço é o mesmo, então a lista não pula quando a primeira foto entra.
 */
function Miniatura({
  codigo,
  fotos,
  nome,
  tamanho = 38,
}: {
  codigo: string
  fotos: number
  nome: string
  tamanho?: number
}) {
  const capa = urlDaCapa(codigo, fotos)

  return (
    <span
      style={{ width: tamanho, height: tamanho }}
      className="flex shrink-0 items-center justify-center overflow-hidden rounded-sistema bg-divisoria text-inativo"
    >
      {capa ? (
        <img src={capa} alt={`Foto de ${nome}`} className="h-full w-full object-cover" />
      ) : (
        <Icone nome="image" tamanho={Math.round(tamanho / 2)} />
      )}
    </span>
  )
}

/**
 * A tira de grade: uma pastilha por tamanho, colorida por risco.
 *
 * A cor vem do `estoque_minimo` DO SKU, não de um limite fixo na tela: loja de
 * tênis trabalha mínimo 1 por numeração e loja de camiseta trabalha 4. Um "≤ 2"
 * cravado aqui acenderia âmbar na loja errada.
 */
function TiraDeGrade({ tira }: { tira: PosicaoGrade[] }) {
  if (tira.length === 0) {
    return <span className="text-[12.5px] text-texto-fraco">Sem grade</span>
  }

  const cores = {
    zerado: 'bg-erro-suave text-erro-texto',
    baixo: 'bg-atencao-suave text-atencao-texto',
    ok: 'bg-divisoria text-texto',
  }

  return (
    <span className="flex flex-wrap gap-1">
      {tira.map((posicao) => {
        const risco = riscoDoTamanho(posicao)
        const titulo =
          risco === 'zerado'
            ? `${posicao.tamanho} esgotado em ${posicao.cores === 1 ? 'sua única cor' : `todas as ${posicao.cores} cores`}`
            : risco === 'baixo'
              ? `${posicao.tamanho} no mínimo ou abaixo (mín ${posicao.estoque_minimo})`
              : `${posicao.tamanho} com saldo saudável (mín ${posicao.estoque_minimo})`

        return (
          <span
            key={posicao.tamanho}
            title={titulo}
            className={`tabular inline-flex min-w-[34px] justify-center rounded-[6px] px-1.5 py-[3px] text-[11.5px] font-bold ${cores[risco]}`}
          >
            {posicao.tamanho} {posicao.saldo}
          </span>
        )
      })}
    </span>
  )
}

/** Bolinhas sobrepostas de cor, no máximo quatro e o resto como "+N". */
function BolinhasDeCor({ cores, tamanho = 14 }: { cores: Cor[]; tamanho?: number }) {
  const visiveis = cores.slice(0, 4)
  const resto = cores.length - visiveis.length

  return (
    <span className="flex items-center">
      {visiveis.map((cor, i) => (
        <span
          key={cor.nome}
          title={cor.nome}
          style={{
            width: tamanho,
            height: tamanho,
            background: cor.hex || '#F3F4F6',
            marginLeft: i === 0 ? 0 : -4,
          }}
          className="inline-block shrink-0 rounded-full border-[1.5px] border-white shadow-[0_0_0_1px_rgba(0,0,0,0.10)]"
        />
      ))}
      {resto > 0 && (
        <span className="tabular ml-1.5 text-[11px] text-texto-suave">+{resto}</span>
      )}
    </span>
  )
}

function SeloDeSituacao({ situacao, rotulo }: { situacao: Situacao; rotulo: string }) {
  const estilos: Record<Situacao, { fundo: string; ponto: string }> = {
    ativo: { fundo: 'bg-ok-suave text-ok-texto', ponto: 'bg-ok' },
    grade_furada: { fundo: 'bg-atencao-suave text-atencao-texto', ponto: 'bg-atencao' },
    esgotado: { fundo: 'bg-erro-suave text-erro-texto', ponto: 'bg-erro' },
  }
  const estilo = estilos[situacao]

  return (
    <span
      className={`inline-flex items-center gap-[7px] rounded-full px-2.5 py-1 text-[12.5px] font-semibold whitespace-nowrap ${estilo.fundo}`}
    >
      <span aria-hidden className={`h-1.5 w-1.5 rounded-full ${estilo.ponto}`} />
      {rotulo}
    </span>
  )
}

/**
 * Margem colorida.
 *
 * O corte de 45 % é a meta do vestuário que a spec 01 e o protótipo usam. É
 * ORIENTAÇÃO: a `min_margin_pct` por categoria é da tela de categorias, que
 * não existe ainda, e a spec 05 é explícita que margem abaixo da meta avisa e
 * não bloqueia.
 */
const METAS_DE_MARGEM = 4500

function SeloDeMargem({ pontos }: { pontos?: number }) {
  if (pontos === undefined) {
    return (
      <span className="text-[12.5px] text-texto-fraco" title="Sem preço, não há margem">
        —
      </span>
    )
  }

  const abaixo = pontos < METAS_DE_MARGEM
  return (
    <span
      title={abaixo ? 'Abaixo da meta de 45% do vestuário' : 'Dentro da meta de 45%'}
      className={`tabular text-[13.5px] font-bold ${abaixo ? 'text-atencao-texto' : 'text-ok'}`}
    >
      {formatarPontos(pontos)}
    </span>
  )
}

/* --------------------------------------------------------------- cartões */

function CartoesDeProduto({
  produtos,
  aoAbrir,
}: {
  produtos: LinhaProduto[]
  aoAbrir: (codigo: string) => void
}) {
  return (
    // `minmax(0,1fr)` e nunca `1fr`: regra 1 do "layout que já custou bug".
    <div className="grid gap-4 p-5 [grid-template-columns:repeat(auto-fill,minmax(230px,1fr))]">
      {produtos.map((produto) => (
        <button
          key={produto.codigo}
          type="button"
          onClick={() => aoAbrir(produto.codigo)}
          className="overflow-hidden rounded-sistema border border-borda bg-superficie text-left"
        >
          <span className="relative flex h-[150px] items-center justify-center overflow-hidden bg-divisoria text-inativo">
            {produto.fotos > 0 ? (
              <img
                src={urlDaCapa(produto.codigo, produto.fotos) ?? ''}
                alt={`Foto de ${produto.nome}`}
                className="h-full w-full object-cover"
              />
            ) : (
              <Icone nome="image" tamanho={30} />
            )}
            <span className="absolute top-2.5 left-2.5">
              <SeloDeSituacao situacao={produto.situacao} rotulo={produto.situacao_rotulo} />
            </span>
            {!produto.ativo && (
              <span className="absolute top-2.5 right-2.5 flex items-center gap-1 rounded-full bg-tinta/85 px-2 py-0.5 text-[11px] font-bold text-white">
                <Icone nome="lock" tamanho={12} />
                Inativo
              </span>
            )}
          </span>
          <span className="block px-4 py-3.5">
            <span className="mb-[3px] block truncate text-[13.5px] font-semibold text-tinta">
              {produto.nome}
            </span>
            <span className="tabular mb-2.5 block text-[11.5px] text-texto-suave">
              {produto.codigo}
            </span>
            <span className="mb-2.5 flex items-center justify-between gap-2">
              <span className="tabular text-base font-bold text-tinta">
                {formatarMoeda(produto.preco_centavos)}
              </span>
              <BolinhasDeCor cores={produto.cores} tamanho={15} />
            </span>
            <span className="flex items-center justify-between gap-2 border-t border-divisoria pt-2.5">
              <span
                className={`tabular text-xs ${produto.pecas === 0 ? 'text-erro' : 'text-texto-suave'}`}
              >
                {produto.pecas} em estoque
              </span>
              <span className="tabular text-xs text-texto-suave">
                {produto.tira_grade.length} tam.
              </span>
            </span>
          </span>
        </button>
      ))}
    </div>
  )
}

/* ----------------------------------------------------------- estado vazio */

function EstadoVazio({
  busca,
  temFiltro,
  podeCadastrar,
}: {
  busca: string
  temFiltro: boolean
  podeCadastrar: boolean
}) {
  const filtrando = busca !== '' || temFiltro

  return (
    <div className="flex flex-col items-center gap-2.5 px-4 py-14 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-fundo text-inativo">
        <Icone nome="sell" tamanho={22} />
      </span>
      <p className="text-sm font-semibold text-tinta">
        {filtrando ? 'Nenhum produto encontrado' : 'Nenhum produto cadastrado ainda'}
      </p>
      <p className="max-w-[360px] text-[13px] leading-[1.55] text-texto-suave">
        {filtrando
          ? 'Ajuste a busca, troque a categoria ou limpe o filtro de situação.'
          : 'Um produto já gera a grade inteira de cor × tamanho, com código de barras por variação.'}
      </p>
      {!filtrando && podeCadastrar && (
        <div className="mt-1">
          <LinkBotao para="/app/produtos/novo" icone="add">
            Cadastrar produto
          </LinkBotao>
        </div>
      )}
    </div>
  )
}

/* --------------------------------------------------------------- drawer */

function DrawerDaFicha({
  codigo,
  filial,
  podeEditar,
  aoFechar,
}: {
  codigo: string
  filial: string
  podeEditar: boolean
  aoFechar: () => void
}) {
  const [ficha, setFicha] = useState<Ficha | null>(null)
  const [erro, setErro] = useState('')

  useEffect(() => {
    const controle = new AbortController()
    setFicha(null)

    carregarFicha(codigo, filial, controle.signal)
      .then((dados) => {
        setFicha(dados)
        setErro('')
      })
      .catch((causa: unknown) => {
        if (causa instanceof DOMException && causa.name === 'AbortError') return
        setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível carregar o produto.')
      })

    return () => controle.abort()
  }, [codigo, filial])

  /**
   * Atualiza os códigos alternativos de UMA variação sem recarregar a ficha
   * inteira — a escrita já devolve a lista completa, e um refetch só para
   * refletir isso jogaria fora o que a resposta já trouxe.
   */
  const atualizarCodigosDaVariacao = (skuCanonico: string, codigos: string[]) => {
    setFicha((atual) =>
      atual
        ? {
            ...atual,
            variacoes: atual.variacoes.map((v) =>
              v.codigo_barras === skuCanonico ? { ...v, codigos_alternativos: codigos } : v,
            ),
          }
        : atual,
    )
  }

  // Esc, clique no fundo, X, foco preso e foco devolvido: tudo isto vem da
  // `Gaveta` compartilhada. Esta tela tinha a sua própria moldura, que fazia
  // os três primeiros e nenhum dos dois últimos.
  return (
    <Gaveta rotulo={`Ficha de ${ficha?.nome ?? codigo}`} largura="tabela" aoFechar={aoFechar}>
      <CabecalhoDaGaveta
        titulo={ficha?.nome ?? 'Carregando…'}
        subtitulo={
          ficha ? `${ficha.codigo} · ${ficha.skus} ${ficha.skus === 1 ? 'SKU' : 'SKUs'}` : ''
        }
        rotuloFechar="Fechar ficha"
        aoFechar={aoFechar}
      />

      <CorpoDaGaveta>
        {erro ? (
          <div role="alert" className="rounded-sistema bg-erro-suave px-4 py-3 text-sm text-erro-texto">
            {erro}
          </div>
        ) : !ficha ? (
          <div className="h-[300px] animate-pulse rounded-sistema bg-superficie-suave" />
        ) : (
          <CorpoDaFicha
            ficha={ficha}
            podeEditar={podeEditar}
            aoAtualizarCodigos={atualizarCodigosDaVariacao}
          />
        )}
      </CorpoDaGaveta>

      <RodapeDaGaveta>
        <Botao variante="secundario" onClick={aoFechar}>
          Fechar
        </Botao>
        {/* O botão que o protótipo desenhava e a tela não tinha: até a
            migração 0013 não existia `PUT /produtos/{codigo}` (ver
            docs/de-para.md). Agora existe. */}
        {podeEditar && (
          <LinkBotao
            para={`/app/produtos/${encodeURIComponent(codigo)}/editar`}
            icone="edit_note"
          >
            Editar produto
          </LinkBotao>
        )}
      </RodapeDaGaveta>
    </Gaveta>
  )
}

function CorpoDaFicha({
  ficha,
  podeEditar,
  aoAtualizarCodigos,
}: {
  ficha: Ficha
  podeEditar: boolean
  aoAtualizarCodigos: (skuCanonico: string, codigos: string[]) => void
}) {
  const { grade, venda } = ficha.desempenho
  // `null` fechado; um índice (posição em `ficha.fotos`, que é a mesma coisa
  // que `ordem`) abre o visualizador nela.
  const [fotoAberta, setFotoAberta] = useState<number | null>(null)

  return (
    <div>
      <div className="mb-5 flex gap-3.5">
        {ficha.fotos.length > 0 ? (
          <button
            type="button"
            onClick={() => setFotoAberta(0)}
            aria-label={`Ver foto de ${ficha.nome} em tamanho grande`}
            className="shrink-0 rounded-sistema focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-marca"
          >
            <Miniatura codigo={ficha.codigo} fotos={ficha.fotos.length} nome={ficha.nome} tamanho={80} />
          </button>
        ) : (
          <Miniatura codigo={ficha.codigo} fotos={ficha.fotos.length} nome={ficha.nome} tamanho={80} />
        )}
        <div className="min-w-0">
          <h2 className="font-titulo text-[18px] font-semibold text-titulo">{ficha.nome}</h2>
          <p className="tabular mt-1 text-[13px] text-texto-suave">
            {ficha.codigo}
            {ficha.marca ? ` · ${ficha.marca}` : ''} · {ficha.categoria_rotulo}
          </p>
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            <SeloDeSituacao situacao={ficha.situacao} rotulo={ficha.situacao_rotulo} />
            {!ficha.ativo && (
              <span className="flex items-center gap-1.5 rounded-full bg-divisoria px-2.5 py-1 text-[12.5px] font-semibold text-texto-suave">
                <Icone nome="lock" tamanho={13} />
                Inativo
              </span>
            )}
          </div>
        </div>
      </div>

      {/* As demais fotos, quando existem. A capa já está no cabeçalho; repetir
          as duas coisas gastaria a altura do drawer sem dizer nada novo. */}
      {ficha.fotos.length > 1 && (
        <div className="mb-5 flex flex-wrap gap-2">
          {ficha.fotos.slice(1).map((foto) => (
            <button
              key={foto.ordem}
              type="button"
              onClick={() => setFotoAberta(foto.ordem)}
              aria-label={`Ver foto ${foto.ordem + 1} de ${ficha.nome} em tamanho grande`}
              className="rounded-sistema focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-marca"
            >
              <img
                src={urlDaFoto(ficha.codigo, foto.ordem)}
                alt={foto.nome_arquivo}
                className="h-[56px] w-[56px] rounded-sistema border border-borda object-cover"
              />
            </button>
          ))}
        </div>
      )}

      <div className="mb-6 grid gap-2.5 [grid-template-columns:repeat(auto-fit,minmax(0,1fr))]">
        <CartaoDeValor rotulo="Preço" valor={formatarMoeda(ficha.preco_centavos)} />
        {ficha.custos && (
          <CartaoDeValor rotulo="Custo" valor={formatarMoeda(ficha.custos.custo_centavos)} />
        )}
        {ficha.custos?.margem_pontos_base !== undefined && (
          <CartaoDeValor
            rotulo="Margem"
            valor={formatarPontos(ficha.custos.margem_pontos_base)}
          />
        )}
        <CartaoDeValor rotulo="Estoque" valor={String(ficha.pecas)} />
      </div>

      <Secao titulo="Grade">
        <MatrizDaFicha ficha={ficha} />
      </Secao>

      <Secao
        titulo={
          ficha.skus_inativos > 0
            ? `Variações · ${ficha.skus} ${ficha.skus === 1 ? 'ativa' : 'ativas'}, ${ficha.skus_inativos} fora da grade`
            : `Variações · ${ficha.skus} ${ficha.skus === 1 ? 'SKU' : 'SKUs'}`
        }
      >
        <ListaDeVariacoes
          ficha={ficha}
          podeEditar={podeEditar}
          aoAtualizarCodigos={aoAtualizarCodigos}
        />
      </Secao>

      <Secao titulo="Desempenho">
        <ul className="list-none">
          <ItemDeLista rotulo="Tamanhos zerados">
            {grade.tamanhos_zerados} de {grade.tamanhos}
          </ItemDeLista>
          <ItemDeLista rotulo="Posições da grade com saldo">
            {grade.posicoes_com_saldo} de {grade.posicoes} ·{' '}
            {formatarPontos(grade.cobertura_pontos_base)}
          </ItemDeLista>
        </ul>

        {/*
          Giro, vendas 30d e curva ABC dependem do módulo de venda, que não
          existe. Em vez de mostrar zero — que pareceria medição —, a tela diz
          o que falta. É o mesmo desenho do painel.
        */}
        {venda.fonte === 'aguarda_venda' && (
          <div className="mt-3 flex gap-3 rounded-sistema border border-borda bg-fundo px-3.5 py-3">
            <Icone nome="info" tamanho={19} className="text-texto-fraco" />
            <p className="text-[12.5px] leading-[1.55] text-texto-suave">
              <strong className="font-semibold text-texto">
                Giro, vendas de 30 dias e cobertura em dias
              </strong>{' '}
              aparecem quando existir venda registrada.{' '}
              {venda.espera ?? 'O módulo de PDV ainda não faz parte do sistema.'}
            </p>
          </div>
        )}
      </Secao>

      {ficha.aviso_grade && (
        <div className="flex gap-3 rounded-sistema border border-atencao-suave bg-atencao-fundo px-4 py-3.5">
          <Icone nome="warning" tamanho={20} className="text-atencao-texto" />
          <p className="text-[13px] leading-[1.55] text-atencao-texto">{ficha.aviso_grade}</p>
        </div>
      )}

      {fotoAberta !== null && (
        <VisualizadorDeFotos
          codigo={ficha.codigo}
          nome={ficha.nome}
          fotos={ficha.fotos}
          indiceInicial={fotoAberta}
          aoFechar={() => setFotoAberta(null)}
        />
      )}
    </div>
  )
}

/**
 * Foto do produto em tamanho grande, com navegação entre as demais — o
 * "clicar e abrir em visualização maior" que o app mobile já tinha e a ficha
 * do web não oferecia.
 *
 * Fica num overlay PRÓPRIO, e não em `Dialogo`: aquele componente é uma caixa
 * branca com título e padding — o inverso do que uma foto em tela cheia
 * precisa. O fundo escuro e a imagem sem moldura são o que deixa ver o
 * caimento da peça, não a cromagem do sistema ao redor dela.
 *
 * `indiceInicial` é a `ordem` da foto tocada, e dobra como índice em
 * `ficha.fotos`: a API numera as fotos em sequência a partir de 0 (a capa),
 * então a posição no array e a ordem são o mesmo número — ver `SaidaFotos` no
 * Go ("Devolve a LISTA inteira... a tela não refaz a conta").
 */
/**
 * Fecha SÓ o overlay de cima ao apertar Esc — mesmo efeito de `usarEsc`
 * (`componentes/foco.ts`), mas registrado em fase de CAPTURA e com
 * `stopPropagation`.
 *
 * Existe porque a foto ampliada e os códigos do fabricante são as duas
 * primeiras telas deste sistema a abrir um overlay POR CIMA de uma `Gaveta` já
 * aberta (a ficha do produto). `usarEsc` escuta `keydown` em FASE DE BOLHA sem
 * parar a propagação — e como a `Gaveta` por baixo escuta o MESMO evento do
 * MESMO jeito, um Esc só fecharia as duas de uma vez: a foto (ou o diálogo) E
 * a ficha inteira atrás dela.
 *
 * Fase de captura roda ANTES de fase de bolha para qualquer listener no MESMO
 * nó, não importa a ordem de registro — é isso que garante que este handler
 * intercepta o Esc primeiro, chama `stopPropagation` e impede que o evento
 * chegue ao listener de bolha da gaveta por baixo.
 */
function usarEscDoTopo(aoFechar: () => void) {
  useEffect(() => {
    function aoTeclar(evento: KeyboardEvent) {
      if (evento.key !== 'Escape') return
      evento.stopPropagation()
      aoFechar()
    }
    document.addEventListener('keydown', aoTeclar, true)
    return () => document.removeEventListener('keydown', aoTeclar, true)
  }, [aoFechar])
}

function VisualizadorDeFotos({
  codigo,
  nome,
  fotos,
  indiceInicial,
  aoFechar,
}: {
  codigo: string
  nome: string
  fotos: Foto[]
  indiceInicial: number
  aoFechar: () => void
}) {
  const [indice, setIndice] = useState(indiceInicial)
  const painel = useRef<HTMLDivElement>(null)

  usarFocoPreso(painel)
  // NÃO é `usarEsc` — ver o comentário de `usarEscDoTopo`: este overlay abre
  // POR CIMA da gaveta da ficha, e o Esc de bolha da gaveta fecharia as duas
  // de uma vez.
  usarEscDoTopo(aoFechar)

  const total = fotos.length
  const anterior = () => setIndice((atual) => (atual - 1 + total) % total)
  const proxima = () => setIndice((atual) => (atual + 1) % total)

  // Setas do teclado navegam entre as fotos — o mouse não é a única forma de
  // folhear, e quem já tem foco preso no overlay espera que as setas façam
  // algo.
  useEffect(() => {
    if (total <= 1) return
    function aoTeclar(evento: KeyboardEvent) {
      if (evento.key === 'ArrowLeft') anterior()
      if (evento.key === 'ArrowRight') proxima()
    }
    document.addEventListener('keydown', aoTeclar)
    return () => document.removeEventListener('keydown', aoTeclar)
  }, [total])

  const foto = fotos[indice]
  if (!foto) return null

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Foto de ${nome}`}
      onClick={aoFechar}
      className="fixed inset-0 z-50 flex items-center justify-center bg-[rgba(15,23,42,0.85)] p-6"
    >
      <div
        ref={painel}
        tabIndex={-1}
        onClick={(evento) => evento.stopPropagation()}
        className="relative flex max-h-full max-w-full items-center justify-center outline-none"
      >
        <img
          src={urlDaFoto(codigo, foto.ordem)}
          alt={`Foto ${indice + 1} de ${nome}`}
          className="max-h-[85vh] max-w-[85vw] rounded-sistema object-contain shadow-[0_20px_60px_rgba(0,0,0,0.5)]"
        />

        <button
          type="button"
          onClick={aoFechar}
          aria-label="Fechar"
          className="absolute -top-3 -right-3 flex h-9 w-9 items-center justify-center rounded-full bg-superficie text-tinta shadow-[0_4px_16px_rgba(0,0,0,0.35)] hover:bg-divisoria"
        >
          <Icone nome="close" tamanho={18} />
        </button>

        {total > 1 && (
          <>
            <button
              type="button"
              onClick={anterior}
              aria-label="Foto anterior"
              className="absolute left-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-[rgba(15,23,42,0.55)] text-white hover:bg-[rgba(15,23,42,0.75)]"
            >
              <Icone nome="chevron_esquerda" tamanho={22} />
            </button>
            <button
              type="button"
              onClick={proxima}
              aria-label="Próxima foto"
              className="absolute right-2 top-1/2 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full bg-[rgba(15,23,42,0.55)] text-white hover:bg-[rgba(15,23,42,0.75)]"
            >
              <Icone nome="chevron_direita" tamanho={22} />
            </button>
            <span
              role="status"
              className="absolute -bottom-8 left-1/2 -translate-x-1/2 text-[12.5px] text-white/85"
            >
              {indice + 1} de {total}
            </span>
          </>
        )}
      </div>
    </div>
  )
}

function CartaoDeValor({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="rounded-sistema border border-borda px-3 py-2.5">
      <p className="mb-1 text-[11.5px] text-texto-suave">{rotulo}</p>
      {/* `nowrap` + `shrink-0`: regra 2 do "layout que já custou bug" — número
          em linha de resumo não pode quebrar. */}
      <p className="tabular shrink-0 text-sm font-bold whitespace-nowrap text-tinta">{valor}</p>
    </div>
  )
}

function Secao({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section className="mb-6">
      <h3 className="mb-3 text-[11px] font-bold tracking-[0.6px] text-texto-fraco uppercase">
        {titulo}
      </h3>
      {children}
    </section>
  )
}

function ItemDeLista({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <li className="flex justify-between gap-3 border-t border-divisoria py-2.5 text-[13.5px] first:border-t-0">
      <span className="min-w-0 flex-auto text-texto-suave">{rotulo}</span>
      <span className="tabular shrink-0 font-semibold whitespace-nowrap text-tinta">
        {children}
      </span>
    </li>
  )
}

/**
 * A matriz cor × tamanho do drawer.
 *
 * A spec 01, regra 7, diz que matriz grande vira visão de um item por vez com
 * grupos recolhíveis — e é justamente por isso que a matriz COMPLETA fica aqui,
 * no drawer, e a listagem mostra só a tira agregada.
 */
function MatrizDaFicha({ ficha }: { ficha: Ficha }) {
  const veCusto = ficha.cores.some((cor) =>
    cor.celulas.some((celula) => celula.custo_centavos !== undefined),
  )

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <caption className="sr-only">
          Saldo de {ficha.nome} por cor e tamanho na unidade {ficha.filial.nome}
        </caption>
        <thead>
          <tr>
            <th scope="col" className="pb-2.5 text-left text-xs font-semibold text-texto-suave">
              Cor
            </th>
            {ficha.tamanhos.map((tamanho) => (
              <th
                key={tamanho}
                scope="col"
                className="min-w-[46px] pb-2.5 text-center text-xs font-semibold text-texto-suave"
              >
                {tamanho}
              </th>
            ))}
            <th scope="col" className="pb-2.5 text-right text-xs font-semibold text-texto-suave">
              Total
            </th>
          </tr>
        </thead>
        <tbody>
          {ficha.cores.map((cor) => (
            <tr key={cor.cor}>
              <th
                scope="row"
                className="border-t border-divisoria py-2.5 text-left font-normal"
              >
                <span className="flex items-center gap-2.5">
                  {cor.hex && (
                    <span
                      aria-hidden
                      style={{ background: cor.hex }}
                      className="h-3.5 w-3.5 shrink-0 rounded-full border border-[rgba(0,0,0,0.12)]"
                    />
                  )}
                  <span className="text-[13px] font-medium text-tinta">{cor.cor}</span>
                </span>
              </th>
              {cor.celulas.map((celula, i) => (
                <td key={i} className="border-t border-divisoria py-2.5 text-center">
                  <BadgeDeSaldo celula={celula} veCusto={veCusto} />
                </td>
              ))}
              <td className="tabular border-t border-divisoria py-2.5 text-right text-[13px] font-bold text-tinta">
                {cor.total}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * A LISTA de SKUs do produto.
 *
 * Existe ao lado da matriz porque responde outra pergunta. A matriz diz "onde
 * está faltando"; esta lista diz "qual é o código desta peça" — e é o código
 * que a pessoa precisa para bipar no PDV, conferir uma nota fiscal ou mandar
 * imprimir etiqueta. Antes ele só existia dentro do `title` de uma célula de
 * 46 px, e `title` ninguém copia.
 *
 * A variação DESATIVADA aparece aqui, marcada. Ela continua no banco, o extrato
 * de estoque aponta para ela e a etiqueta dela ainda circula no depósito:
 * escondê-la faria a pessoa procurar um número que tem impresso na mão.
 */
function ListaDeVariacoes({
  ficha,
  podeEditar,
  aoAtualizarCodigos,
}: {
  ficha: Ficha
  podeEditar: boolean
  aoAtualizarCodigos: (skuCanonico: string, codigos: string[]) => void
}) {
  const [avisoDeCopia, setAvisoDeCopia] = useState('')
  const [gerenciando, setGerenciando] = useState<VariacaoFicha | null>(null)

  const copiar = async (codigo: string) => {
    try {
      await navigator.clipboard.writeText(codigo)
      setAvisoDeCopia(`Código ${codigo} copiado.`)
    } catch {
      setAvisoDeCopia('O navegador não deixou copiar. O código está na tabela.')
    }
  }

  if (ficha.variacoes.length === 0) {
    return (
      <p className="text-[13px] text-texto-suave">
        Este produto não tem variação cadastrada. A grade é criada na edição do produto.
      </p>
    )
  }

  const colunas = ['Variação', 'Código de barras', 'Saldo', 'Preço']
  if (ficha.ve_custo) colunas.push('Custo')

  return (
    <>
      {/* Wrapper com `overflow-x-auto` + `min-width` na tabela: regra 6 do
          "layout que já custou bug". O drawer tem 520 px, e cinco colunas com
          um EAN-13 no meio não cabem sem rolagem. */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[480px] border-collapse">
          <caption className="sr-only">
            SKUs de {ficha.nome}, com código de barras, saldo e preço por variação
          </caption>
          <thead>
            <tr>
              {colunas.map((coluna) => (
                <th
                  key={coluna}
                  scope="col"
                  className={`pb-2 text-xs font-semibold text-texto-suave ${
                    coluna === 'Saldo' || coluna === 'Preço' || coluna === 'Custo'
                      ? 'text-right'
                      : 'text-left'
                  }`}
                >
                  {coluna}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ficha.variacoes.map((variacao) => (
              <tr
                key={variacao.codigo_barras}
                className={variacao.ativo ? undefined : 'bg-superficie-suave'}
              >
                <th
                  scope="row"
                  className="border-t border-divisoria py-2 pr-3 text-left font-normal"
                >
                  <span className="flex items-center gap-2">
                    {variacao.hex && (
                      <span
                        aria-hidden
                        style={{ background: variacao.hex }}
                        className="h-3.5 w-3.5 shrink-0 rounded-full border border-[rgba(0,0,0,0.12)]"
                      />
                    )}
                    <span className="text-[13px] font-medium text-tinta">
                      {variacao.cor} · {variacao.tamanho}
                    </span>
                  </span>
                  {/* Fora da grade, e não "inativa": o que aconteceu é que a
                      variação saiu da grade vendável. As peças dela continuam
                      contadas no estoque. */}
                  {!variacao.ativo && (
                    <span className="mt-1 flex w-fit items-center gap-1 rounded-full bg-divisoria px-2 py-0.5 text-[11px] font-semibold text-texto-suave">
                      <Icone nome="lock" tamanho={12} />
                      Fora da grade
                    </span>
                  )}
                </th>

                <td className="border-t border-divisoria py-2 pr-3">
                  <span className="flex items-center gap-1.5">
                    {/* `tabular` porque é número lido em voz alta e conferido
                        dígito a dígito contra a etiqueta. */}
                    <span className="tabular text-[13px] text-texto select-all">
                      {variacao.codigo_barras}
                    </span>
                    <button
                      type="button"
                      onClick={() => void copiar(variacao.codigo_barras)}
                      aria-label={`Copiar o código de barras de ${variacao.cor} ${variacao.tamanho}`}
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-sistema text-texto-fraco hover:bg-divisoria hover:text-texto"
                    >
                      <Icone nome="copiar" tamanho={15} />
                    </button>
                    {podeEditar && (
                      <button
                        type="button"
                        onClick={() => setGerenciando(variacao)}
                        aria-label={`Códigos de barras do fabricante de ${variacao.cor} ${variacao.tamanho}`}
                        title="Códigos do fabricante"
                        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-sistema text-texto-fraco hover:bg-divisoria hover:text-texto"
                      >
                        <Icone nome="barcode_scanner" tamanho={15} />
                      </button>
                    )}
                  </span>
                  {/* Os códigos do fabricante aparecem AQUI, e não só dentro do
                      diálogo: quem vai bipar no PDV precisa saber que a peça
                      também responde por eles sem abrir mais nada. */}
                  {variacao.codigos_alternativos && variacao.codigos_alternativos.length > 0 && (
                    <span className="mt-1 flex flex-wrap gap-1">
                      {variacao.codigos_alternativos.map((alt) => (
                        <span
                          key={alt}
                          className="tabular rounded-full bg-divisoria px-2 py-0.5 text-[11px] text-texto-suave"
                        >
                          {alt}
                        </span>
                      ))}
                    </span>
                  )}
                </td>

                <td className="tabular border-t border-divisoria py-2 text-right text-[13px]">
                  <span
                    className={
                      variacao.saldo === 0
                        ? 'font-semibold text-erro'
                        : variacao.saldo <= variacao.estoque_minimo
                          ? 'font-semibold text-atencao-texto'
                          : 'text-tinta'
                    }
                  >
                    {variacao.saldo}
                  </span>
                </td>

                <td className="tabular border-t border-divisoria py-2 text-right text-[13px] text-tinta">
                  {formatarMoeda(variacao.preco_centavos)}
                </td>

                {ficha.ve_custo && (
                  <td className="tabular border-t border-divisoria py-2 text-right text-[13px] text-texto-suave">
                    {formatarMoeda(variacao.custo_centavos ?? 0)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* `role="status"` e não um toast: a confirmação de cópia é justamente o
          tipo de coisa que quem usa leitor de tela precisa ouvir. */}
      <p role="status" className="mt-2 min-h-[16px] text-[11.5px] text-texto-suave">
        {avisoDeCopia}
      </p>

      {gerenciando && (
        <DialogoCodigosDeFabricante
          produtoCodigo={ficha.codigo}
          variacao={gerenciando}
          aoFechar={() => setGerenciando(null)}
          aoAtualizar={(codigos) => {
            aoAtualizarCodigos(gerenciando.codigo_barras, codigos)
            setGerenciando((atual) => (atual ? { ...atual, codigos_alternativos: codigos } : atual))
          }}
        />
      )}
    </>
  )
}

/**
 * Cadastro dos códigos de barras que já vieram impressos na peça
 * (fabricante) — migração 0025.
 *
 * **Por que isto existe.** Até a 0025 o único código de uma variação era o
 * EAN-13 que o próprio sistema gera. Isso serve para a loja que imprime a
 * própria etiqueta e não serve para quem já tem o estoque etiquetado pelo
 * fabricante: o PDV bipava o EAN de fábrica e o sistema não conhecia peça
 * nenhuma com aquele número.
 *
 * **O código canônico não é substituído.** A peça passa a responder pelos
 * DOIS — decisão de produto que preserva as etiquetas internas já impressas.
 * Por isso ele aparece na lista, marcado, e sem botão de remover: é a
 * identidade da variação em toda a API, e a rota de remoção nem existe para
 * ele.
 *
 * **Sem leitor de câmera aqui.** No app mobile a câmera é o único jeito de
 * ler um código sem digitar; no navegador um leitor de código de barras USB
 * ou Bluetooth já digita no campo focado, como qualquer teclado — é o mesmo
 * padrão que `BuscaDeSKU` e o campo de estoque usam.
 */
function DialogoCodigosDeFabricante({
  produtoCodigo,
  variacao,
  aoFechar,
  aoAtualizar,
}: {
  produtoCodigo: string
  variacao: VariacaoFicha
  aoFechar: () => void
  /** Chamado com a lista COMPLETA depois de cada escrita bem-sucedida. */
  aoAtualizar: (codigos: string[]) => void
}) {
  const [codigos, setCodigos] = useState(variacao.codigos_alternativos ?? [])
  const [novo, setNovo] = useState('')
  const [ocupado, setOcupado] = useState(false)
  const [erro, setErro] = useState('')

  // Este diálogo abre POR CIMA da gaveta da ficha. O `Dialogo` compartilhado já
  // chama `usarEsc(aoFechar)` por dentro — mas em fase de bolha, sem parar a
  // propagação —, e a gaveta escuta o MESMO Esc do mesmo jeito. Sem isto, Esc
  // fecharia o diálogo E a ficha inteira atrás dele de uma vez só. Ver o
  // comentário de `usarEscDoTopo`.
  usarEscDoTopo(aoFechar)

  const cheio = codigos.length >= MAX_CODIGOS_ALTERNATIVOS

  const adicionar = async () => {
    const limpo = novo.trim()
    if (!limpo || ocupado) return

    // O canônico já é desta peça — mandar isto ao servidor voltaria como um
    // 409 contra a própria variação, e a mensagem "já cadastrado em outra
    // peça" seria falsa: ele não está em outra peça, é esta mesma.
    if (limpo === variacao.codigo_barras) {
      setErro('Este já é o código interno desta peça.')
      return
    }

    setOcupado(true)
    setErro('')
    try {
      const saida = await adicionarCodigoAlternativo(produtoCodigo, variacao.codigo_barras, limpo)
      setCodigos(saida.codigos)
      aoAtualizar(saida.codigos)
      setNovo('')
      // Sem `.focus()` manual: o `<input>` de `Campo` mantém a MESMA
      // identidade de DOM entre estas duas chamadas de `setState` (o `id` de
      // `useId()` não muda), então o foco continua nele sozinho — é o que
      // deixa bipar código, Enter, bipar o próximo, Enter, sem tocar no
      // mouse.
    } catch (causa: unknown) {
      // A mensagem do servidor NOMEIA a peça que já ocupa o número — trocá-la
      // por um texto genérico apagaria justamente a parte útil dela.
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível cadastrar o código.')
    } finally {
      setOcupado(false)
    }
  }

  const remover = async (codigo: string) => {
    setOcupado(true)
    setErro('')
    try {
      const saida = await removerCodigoAlternativo(produtoCodigo, variacao.codigo_barras, codigo)
      setCodigos(saida.codigos)
      aoAtualizar(saida.codigos)
    } catch (causa: unknown) {
      setErro(causa instanceof ErroApi ? causa.message : 'Não foi possível remover o código.')
    } finally {
      setOcupado(false)
    }
  }

  return (
    <Dialogo
      titulo="Códigos de barras da peça"
      descricao={`${variacao.cor} · ${variacao.tamanho}`}
      icone="barcode_scanner"
      aoFechar={aoFechar}
    >
      <div className="flex flex-col gap-1.5">
        <LinhaDeCodigo
          codigo={variacao.codigo_barras}
          rotulo="Interno — impresso pela etiqueta do sistema"
        />
        {codigos.map((codigo) => (
          <LinhaDeCodigo
            key={codigo}
            codigo={codigo}
            rotulo="Fabricante"
            aoRemover={ocupado ? undefined : () => void remover(codigo)}
          />
        ))}
      </div>

      <form
        onSubmit={(evento) => {
          evento.preventDefault()
          void adicionar()
        }}
        className="mt-4"
      >
        {cheio ? (
          <p className="text-[12.5px] leading-[1.5] text-texto-suave">
            Esta peça já tem o máximo de {MAX_CODIGOS_ALTERNATIVOS} códigos de fabricante. Remova
            um antes de cadastrar outro.
          </p>
        ) : (
          <div className="flex items-end gap-2.5">
            <div className="flex-1">
              <Campo
                rotulo="Código da etiqueta de fábrica"
                placeholder="Bipe ou digite, ex.: 7891234567895"
                value={novo}
                onChange={(evento) => setNovo(evento.target.value)}
                erro={erro || undefined}
                autoFocus
                disabled={ocupado}
              />
            </div>
            <div className="pb-[1px]">
              <Botao type="submit" variante="secundario" disabled={ocupado || !novo.trim()}>
                {ocupado ? 'Adicionando…' : 'Adicionar'}
              </Botao>
            </div>
          </div>
        )}
      </form>

      <div className="mt-6 flex justify-end">
        <Botao variante="secundario" onClick={aoFechar}>
          Fechar
        </Botao>
      </div>
    </Dialogo>
  )
}

/** Uma linha da lista de códigos — o interno sem remover, o alternativo com. */
function LinhaDeCodigo({
  codigo,
  rotulo,
  aoRemover,
}: {
  codigo: string
  rotulo: string
  aoRemover?: () => void
}) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-sistema border border-borda bg-fundo px-3 py-2">
      <div className="min-w-0">
        <p className="tabular truncate text-[13.5px] font-semibold text-tinta">{codigo}</p>
        <p className="text-[11px] text-texto-fraco">{rotulo}</p>
      </div>
      {aoRemover && (
        <button
          type="button"
          onClick={aoRemover}
          aria-label={`Remover o código ${codigo}`}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-sistema text-texto-fraco hover:bg-divisoria hover:text-erro"
        >
          <Icone nome="close" tamanho={16} />
        </button>
      )}
    </div>
  )
}

/**
 * Badge de saldo de uma posição, com quatro estados.
 *
 * O quarto é o que quase todo sistema erra: "esta cor não vem neste tamanho"
 * desenhado como travessão, não como zero. Zero vermelho numa posição que não
 * existe manda comprar o que o fornecedor não fabrica.
 */
function BadgeDeSaldo({ celula, veCusto }: { celula: CelulaFicha; veCusto: boolean }) {
  const risco = riscoDaCelula(celula)

  if (risco === 'ausente') {
    return (
      <span className="text-[12.5px] text-inativo" title="Esta cor não tem este tamanho">
        —
      </span>
    )
  }

  const cores = {
    zerado: 'bg-erro-suave text-erro-texto',
    baixo: 'bg-atencao-suave text-atencao-texto',
    ok: 'bg-ok-suave text-ok-texto',
  }[risco]

  const situacao =
    risco === 'zerado'
      ? 'Esgotado'
      : risco === 'baixo'
        ? `No mínimo ou abaixo (mín ${celula.estoque_minimo})`
        : `Saldo saudável (mín ${celula.estoque_minimo})`

  // Preço e custo POR SKU entram no title: a matriz não tem largura para
  // três números por célula, e o dado é do SKU, não do produto.
  const dinheiro = veCusto
    ? ` · ${formatarMoeda(celula.preco_centavos)} · custo ${formatarMoeda(celula.custo_centavos ?? 0)}`
    : ` · ${formatarMoeda(celula.preco_centavos)}`

  return (
    <span
      title={situacao + dinheiro}
      className={`tabular inline-flex min-w-[28px] justify-center rounded-full px-2 py-[3px] text-[12.5px] font-semibold ${cores}`}
    >
      {celula.saldo}
    </span>
  )
}

/* ------------------------------------------------------------- utilidades */

function contar(quantidade: number, singular: string, plural: string): string {
  return `${quantidade} ${quantidade === 1 ? singular : plural}`
}

